/**
 * The Month grid's per-day data, as the Practice calendar derives it (PROD-3307 C6): a port of PM
 * `src/scheduling/pages/scheduling-page.tsx` `monthGrid` (origin/main 5a49b124, ~:900-1030).
 *
 * The Month response is shaped unlike Day / Week (Flutter `calendar_by_month_widget.dart`):
 *   - appointments live in `appointmentByGroupBy[]`, one group per day keyed by `appointmentDate` (the backend's local
 *     day anchor). The backend caps each group's `appointmentList` at 3 and reports the overflow in `group.count`, the
 *     RAW "+N more" number (Flutter `getMoreAppointment`), never derived here;
 *   - blocks come in a separate `blockAppointment[]` keyed by `blockDate`, the reason in `note`; they are merged into
 *     their day and sorted by start with the appointments (`getMonthAppointment`).
 * Status: a chosen status is applied server-side (the Month request carries it); with none chosen the client still
 * hides Cancelled (Flutter `calendar_by_month_widget.dart:346-350`), which `filterAppointmentsByStatus` encodes.
 * A flat `appointments` list (a future variant) is bucketed by start day as a fallback.
 */
import type { PACalendarMonthEvent } from '@scripsteam/scrips-signal-ds';
import type { AppointmentsDetailsResponse, ListAppointmentResponse } from '@/types/scheduling-api';
import { filterAppointmentsByStatus, mapAppointmentsToDayList, type DayListRow } from './calendar-mapping';

/** One `blockAppointment[]` entry of the Month response (PM `MonthBlockAppointment`). */
export interface MonthBlockAppointment {
  id: string;
  note?: string | null;
  blockDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  recurringId?: string | null;
}

export type MonthDayData = Map<string, { events: PACalendarMonthEvent[]; moreCount: number }>;

const rowToEvent = (r: DayListRow): PACalendarMonthEvent => ({
  id: r.id,
  label: r.isBlock ? (r.note ?? '') : r.patientName,
  timeRange: r.timeRange,
  profileColor: r.profileColor,
  isBlock: r.isBlock,
  isRecurring: r.isRecurring,
  patientId: r.patientId,
  providerId: r.practitionerId,
  appointmentProfileId: r.appointmentProfileId,
});

const startMs = (iso: string | null | undefined) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? 0 : t;
};

const asResponse = (list: unknown[]) => ({ appointments: list }) as unknown as AppointmentsDetailsResponse;

export function monthDayData(
  monthResponse: AppointmentsDetailsResponse | undefined,
  selectedStatusCodes: string[],
): MonthDayData {
  const byDay = new Map<string, { items: { ms: number; event: PACalendarMonthEvent }[]; moreCount: number }>();
  const bucket = (iso: string) => {
    let b = byDay.get(iso);
    if (!b) {
      b = { items: [], moreCount: 0 };
      byDay.set(iso, b);
    }
    return b;
  };

  const groups = monthResponse?.appointmentByGroupBy ?? [];
  for (const group of groups) {
    const dayISO = (group.appointmentDate ?? '').slice(0, 10);
    if (!dayISO) continue;
    const filtered =
      filterAppointmentsByStatus(asResponse(group.appointmentList ?? []), selectedStatusCodes) ?? asResponse([]);
    const b = bucket(dayISO);
    for (const r of mapAppointmentsToDayList(filtered)) b.items.push({ ms: startMs(r.startISO), event: rowToEvent(r) });
    // The RAW backend count, no subtraction (Flutter `calendar_by_month_widget.dart:165-220`, `getMoreAppointment:395`).
    b.moreCount += group.count ?? 0;
  }

  if (groups.length === 0) {
    const filtered =
      filterAppointmentsByStatus(asResponse(monthResponse?.appointments ?? []), selectedStatusCodes) ?? asResponse([]);
    for (const r of mapAppointmentsToDayList(filtered)) {
      const iso = (r.startISO ?? '').slice(0, 10);
      if (iso) bucket(iso).items.push({ ms: startMs(r.startISO), event: rowToEvent(r) });
    }
  }

  const blocks = (monthResponse?.blockAppointment ?? []) as unknown as MonthBlockAppointment[];
  if (blocks.length > 0) {
    const blockRows = mapAppointmentsToDayList(
      asResponse(
        blocks.map((bl) => ({
          id: bl.id,
          start: bl.startDate,
          end: bl.endDate,
          comment: bl.note,
          isBlock: true,
          recurringId: bl.recurringId ?? undefined,
          participant: [],
        })) as unknown as ListAppointmentResponse[],
      ),
    );
    const rowById = new Map(blockRows.map((r) => [r.id, r]));
    for (const bl of blocks) {
      const dayISO = (bl.blockDate ?? bl.startDate ?? '').slice(0, 10);
      const row = rowById.get(bl.id);
      if (!dayISO || !row) continue;
      bucket(dayISO).items.push({ ms: startMs(bl.startDate), event: rowToEvent(row) });
    }
  }

  const out: MonthDayData = new Map();
  for (const [iso, b] of byDay) {
    b.items.sort((x, y) => x.ms - y.ms);
    out.set(iso, { events: b.items.map((i) => i.event), moreCount: b.moreCount });
  }
  return out;
}
